import { useCallback } from 'react';

// Declare window.IGLOO type
declare global {
  interface Window {
    IGLOO?: {
      getBlackbox?: () => {
        finished: boolean;
        blackbox: string;
      };
      trace_handler?: (message: string) => void;
      enable_rip?: boolean;
      enable_flash?: boolean;
      install_flash?: boolean;
      loader?: {
        version: string;
        fp_static: boolean;
        tp: boolean;
        tp_host: string;
      };
    };
  }
}

// Track if iovation script has been loaded globally
let iovationScriptLoaded = false;
let iovationScriptLoading = false;

/**
 * Dynamically load the iovation script
 * @returns Promise that resolves when script is loaded
 */
const loadIovationScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (iovationScriptLoaded) {
      resolve();
      return;
    }

    // Currently loading, wait for it
    if (iovationScriptLoading) {
      const checkLoaded = setInterval(() => {
        if (iovationScriptLoaded) {
          clearInterval(checkLoaded);
          resolve();
        }
      }, 50);
      return;
    }

    iovationScriptLoading = true;

    // Initialize IGLOO config before loading script
    window.IGLOO = window.IGLOO || {
      enable_rip: true,
      enable_flash: false,
      install_flash: false,
      loader: {
        version: 'general5',
        fp_static: true,
        tp: true,
        tp_host: 'https://mpsnare.iesnare.com',
      },
    };

    // Create and inject script element
    const script = document.createElement('script');
    script.src = '/static/js/iovation.js';
    script.async = true;

    script.onload = () => {
      iovationScriptLoaded = true;
      iovationScriptLoading = false;
      resolve();
    };

    script.onerror = () => {
      iovationScriptLoading = false;
      reject(new Error('Failed to load iovation script'));
    };

    document.body.appendChild(script);
  });
};

// Poll getBlackbox with Fibonacci-like backoff until the blackbox is
// finished, so a complete key is ready by the time a submit reads it. The
// value is NOT sent anywhere from here: submit payloads read it fresh via
// window.IGLOO.getBlackbox() and carry it as customData.iovationBlackbox.
// (The old flow POSTed it into a server cookie, but a finished blackbox
// routinely exceeds the ~4KB browser cookie cap, so only stale or unfinished
// partial keys ever survived that transport.)
const warmUpBlackbox = () => {
  let previousDelay = 0;
  let currentDelay = 10;
  const maxTotalTime = 10000; // 10 seconds
  let elapsedTime = 0;

  const getNextDelay = () => {
    const nextDelay = previousDelay + currentDelay;
    previousDelay = currentDelay;
    currentDelay = Math.min(nextDelay, 1000); // Cap individual delay at 1 second
    return currentDelay;
  };

  const attemptGeneration = () => {
    if (typeof window.IGLOO?.getBlackbox !== 'function') {
      if (elapsedTime < maxTotalTime) {
        const delay = getNextDelay();
        elapsedTime += delay;
        setTimeout(attemptGeneration, delay);
      } else {
        console.error(
          'iovation getBlackbox failed to initialize after max time'
        );
      }
      return;
    }

    const bbData = window.IGLOO.getBlackbox();

    if (!bbData.finished && elapsedTime < maxTotalTime) {
      const delay = getNextDelay();
      elapsedTime += delay;
      setTimeout(attemptGeneration, delay);
    }
  };

  setTimeout(attemptGeneration, 10);
};

/**
 * Custom hook to handle iovation fingerprinting
 * Lazily loads the iovation script only when initializeIovation is called,
 * then polls until the blackbox is finished so submit-time reads get a
 * complete key.
 * @returns Object with initialization function
 */
export const useIovationFingerprint = () => {
  const initializeIovation = useCallback(() => {
    // Lazily load the iovation script, then warm up the blackbox
    loadIovationScript()
      .then(() => {
        window.IGLOO = window.IGLOO || {};
        window.IGLOO.trace_handler = function (message) {
          // console.log('iovation trace:', message);
        };

        warmUpBlackbox();
      })
      .catch((error) => {
        console.error('Error loading iovation:', error);
      });
  }, []);

  return {
    initializeIovation,
  };
};
